import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell, inputCls } from "@/components/AdminTools";
import { formatPhone } from "@/lib/format";
import {
  Building2,
  Search,
  Workflow as WorkflowIcon,
  CalendarDays,
  Clock,
  Mail,
  Phone,
  Palette,
  RefreshCw,
  Users,
  CheckCircle2,
  Moon,
  Sparkles,
  Activity,
  GraduationCap,
  LogIn,
} from "lucide-react";

// --- helpers --------------------------------------------------------------

function _fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "—";
  }
}

function _ago(iso) {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  const diff = Math.max(0, Date.now() - t);
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d ago`;
  return _fmtDate(iso);
}

// Status pill — lightweight, no extra dep
const STATUS = {
  active: {
    label: "Active",
    cls: "bg-emerald-100 text-emerald-800",
    Icon: CheckCircle2,
  },
  dormant: {
    label: "Dormant",
    cls: "bg-amber-100 text-amber-800",
    Icon: Moon,
  },
  fresh: {
    label: "Fresh",
    cls: "bg-blue-100 text-blue-800",
    Icon: Sparkles,
  },
};

function StatusPill({ status }) {
  const meta = STATUS[status] || STATUS.fresh;
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${meta.cls}`}
      data-testid={`clinic-status-${status}`}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

// Health-score cell. Blends workflow throughput (40) + training (30) +
// login recency (30) into a 0–100 number with a tiny breakdown popover so
// admins know *why* a clinic is red/amber/emerald — not just *that* it is.
function _healthTone(score) {
  if (score >= 70) return { ring: "ring-emerald-200", bg: "bg-emerald-50", text: "text-emerald-800", bar: "bg-emerald-500", label: "Healthy" };
  if (score >= 40) return { ring: "ring-amber-200", bg: "bg-amber-50", text: "text-amber-800", bar: "bg-amber-500", label: "At risk" };
  return { ring: "ring-rose-200", bg: "bg-rose-50", text: "text-rose-800", bar: "bg-rose-500", label: "Critical" };
}

function HealthCell({ score = 0, breakdown }) {
  const [open, setOpen] = useState(false);
  const tone = _healthTone(score);
  const b = breakdown || {};
  const components = [
    {
      key: "workflow",
      label: "Workflow throughput",
      icon: WorkflowIcon,
      pts: b.workflow_pts || 0,
      max: b.workflow_max || 40,
      detail: `${b.workflow_30d_count || 0} workflow${(b.workflow_30d_count || 0) === 1 ? "" : "s"} in last 30d`,
    },
    {
      key: "training",
      label: "Training completion",
      icon: GraduationCap,
      pts: b.training_pts || 0,
      max: b.training_max || 30,
      detail: `${b.training_completed || 0} of ${b.training_total || 0} modules completed`,
    },
    {
      key: "recency",
      label: "Login recency",
      icon: LogIn,
      pts: b.recency_pts || 0,
      max: b.recency_max || 30,
      detail: b.has_login ? "Last login on file" : "No login on file (grace credit)",
    },
  ];
  return (
    <div
      className="relative inline-block"
      onMouseEnter={() => setOpen(true)}
      onMouseLeave={() => setOpen(false)}
      onFocus={() => setOpen(true)}
      onBlur={() => setOpen(false)}
    >
      <button
        type="button"
        data-testid="clinic-health-pill"
        className={`inline-flex items-center gap-2 rounded-full px-2.5 py-1 ring-1 ${tone.bg} ${tone.ring} ${tone.text} text-xs font-bold cursor-default`}
        title={`${tone.label} · ${score}/100`}
      >
        <Activity className="w-3 h-3" />
        <span className="tabular-nums">{score}</span>
        <span className="w-10 h-1.5 rounded-full bg-white/70 overflow-hidden">
          <span
            className={`block h-full ${tone.bar}`}
            style={{ width: `${score}%` }}
          />
        </span>
      </button>
      {open && (
        <div
          className="absolute z-30 right-0 mt-2 w-72 bg-white border border-slate-200 rounded-2xl shadow-xl p-3 text-left"
          data-testid="clinic-health-popover"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
              Health score
            </div>
            <span
              className={`text-[10px] font-bold uppercase tracking-wider ${tone.text}`}
            >
              {tone.label}
            </span>
          </div>
          <div className="space-y-2">
            {components.map((c) => {
              const Icon = c.icon;
              const pctOfMax = c.max ? Math.round((c.pts / c.max) * 100) : 0;
              return (
                <div key={c.key} data-testid={`clinic-health-component-${c.key}`}>
                  <div className="flex items-center justify-between text-[11px] mb-0.5">
                    <span className="inline-flex items-center gap-1.5 text-slate-700 font-semibold">
                      <Icon className="w-3 h-3 text-slate-400" />
                      {c.label}
                    </span>
                    <span className="tabular-nums text-slate-900 font-bold">
                      {c.pts}
                      <span className="text-slate-400 font-normal">/{c.max}</span>
                    </span>
                  </div>
                  <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className={`h-full ${c.pts > 0 ? tone.bar : "bg-slate-200"}`}
                      style={{ width: `${pctOfMax}%` }}
                    />
                  </div>
                  <div className="text-[10px] text-slate-500 mt-0.5">{c.detail}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500 leading-snug">
            Score = workflows (last 30d) + training completion + login recency.
            Drops below 40 → flag for outreach.
          </div>
        </div>
      )}
    </div>
  );
}

// --- page -----------------------------------------------------------------

export default function AdminClinicRoster() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [sortByHealth, setSortByHealth] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/clinic-roster");
      setRows(r.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  // KPI rollups across the whole roster (not filtered) so the header reflects
  // the real customer base while admins narrow the table.
  const totals = useMemo(() => {
    const t = { all: rows.length, active: 0, dormant: 0, fresh: 0, workflows: 0, atrisk: 0, healthy: 0, critical: 0 };
    for (const r of rows) {
      t[r.status] = (t[r.status] || 0) + 1;
      t.workflows += r.workflow_count || 0;
      const score = r.health_score || 0;
      if (score >= 70) t.healthy += 1;
      else if (score >= 40) t.atrisk += 1;
      else t.critical += 1;
    }
    return t;
  }, [rows]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const out = rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!needle) return true;
      return (
        (r.clinic_name || "").toLowerCase().includes(needle) ||
        (r.owner_name || "").toLowerCase().includes(needle) ||
        (r.owner_email || "").toLowerCase().includes(needle)
      );
    });
    if (sortByHealth) {
      out.sort((a, b) => (a.health_score || 0) - (b.health_score || 0));
    }
    return out;
  }, [rows, q, status, sortByHealth]);

  return (
    <ToolPageShell
      eyebrow="Admin · Clinics · Roster"
      title="Clinic roster"
      intro="Every clinical customer subscribing to Nodera, with subscription health, workflow throughput, and a direct lane into branding and operations."
      actions={
        <button
          onClick={load}
          data-testid="clinic-roster-refresh"
          className="inline-flex items-center gap-1.5 bg-white border border-slate-200 text-slate-700 rounded-full px-4 py-2 text-sm font-semibold hover:bg-slate-50"
        >
          <RefreshCw className="w-3.5 h-3.5" /> Refresh
        </button>
      }
    >
      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
        <KpiCard
          icon={Users}
          label="Total clinics"
          value={totals.all}
          tone="slate"
          testid="clinic-kpi-total"
        />
        <KpiCard
          icon={CheckCircle2}
          label="Healthy (70+)"
          value={totals.healthy}
          tone="emerald"
          testid="clinic-kpi-healthy"
        />
        <KpiCard
          icon={Activity}
          label="At risk (40-69)"
          value={totals.atrisk}
          tone="amber"
          testid="clinic-kpi-atrisk"
        />
        <KpiCard
          icon={Moon}
          label="Critical (<40)"
          value={totals.critical}
          tone="rose"
          testid="clinic-kpi-critical"
        />
        <KpiCard
          icon={WorkflowIcon}
          label="Workflows · all-time"
          value={totals.workflows}
          tone="blue"
          testid="clinic-kpi-workflows"
        />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="relative flex-1 min-w-[260px]">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search clinic name, owner, or email"
            data-testid="clinic-roster-search"
            className={`${inputCls} pl-9`}
          />
        </div>
        <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-full p-1">
          {[
            { key: "all", label: `All · ${totals.all}` },
            { key: "active", label: `Active · ${totals.active}` },
            { key: "dormant", label: `Dormant · ${totals.dormant}` },
            { key: "fresh", label: `Fresh · ${totals.fresh}` },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setStatus(t.key)}
              data-testid={`clinic-roster-filter-${t.key}`}
              className={`px-3 py-1.5 text-xs font-semibold rounded-full transition-colors ${
                status === t.key
                  ? "bg-slate-900 text-white"
                  : "text-slate-600 hover:bg-slate-100"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          type="button"
          onClick={() => setSortByHealth((v) => !v)}
          data-testid="clinic-roster-sort-health"
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold border transition-colors ${
            sortByHealth
              ? "bg-rose-50 border-rose-200 text-rose-700"
              : "bg-white border-slate-200 text-slate-700 hover:bg-slate-50"
          }`}
          title="Surface clinics needing attention first"
        >
          <Activity className="w-3.5 h-3.5" />
          {sortByHealth ? "At-risk first" : "Sort by health"}
        </button>
      </div>

      {/* Table */}
      <div
        className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
        data-testid="clinic-roster-table"
      >
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 text-[10px] uppercase tracking-wider font-bold text-slate-500">
                <th className="text-left px-4 py-3">Clinic</th>
                <th className="text-left px-4 py-3">Owner</th>
                <th className="text-left px-4 py-3">Contact</th>
                <th className="text-left px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Health</th>
                <th className="text-right px-4 py-3">Workflows</th>
                <th className="text-left px-4 py-3">Last activity</th>
                <th className="text-left px-4 py-3">Joined</th>
                <th className="text-right px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-12 text-center text-sm text-slate-500"
                    data-testid="clinic-roster-loading"
                  >
                    Loading roster…
                  </td>
                </tr>
              ) : filtered.length === 0 ? (
                <tr>
                  <td
                    colSpan={9}
                    className="px-4 py-12 text-center text-sm text-slate-500"
                    data-testid="clinic-roster-empty"
                  >
                    {rows.length === 0
                      ? "No clinic customers yet. Approve a Clinic Access Request to onboard one."
                      : "No clinics match those filters."}
                  </td>
                </tr>
              ) : (
                filtered.map((r) => (
                  <tr
                    key={r.id}
                    className="border-t border-slate-100 hover:bg-slate-50/60 transition-colors"
                    data-testid={`clinic-roster-row-${r.id}`}
                  >
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2.5">
                        <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0 overflow-hidden">
                          {r.logo_url ? (
                            <img
                              src={r.logo_url}
                              alt=""
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <Building2 className="w-4 h-4 text-slate-500" />
                          )}
                        </div>
                        <div>
                          <div className="font-semibold text-slate-900 leading-tight">
                            {r.clinic_name}
                          </div>
                          {r.address && (
                            <div className="text-[11px] text-slate-500 leading-tight mt-0.5 truncate max-w-[200px]">
                              {r.address}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-slate-700">{r.owner_name}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5 text-[12px]">
                        <a
                          href={`mailto:${r.owner_email}`}
                          className="inline-flex items-center gap-1 text-slate-700 hover:text-blue-700"
                        >
                          <Mail className="w-3 h-3 text-slate-400" />
                          <span className="truncate max-w-[180px]">{r.owner_email}</span>
                        </a>
                        {r.phone && (
                          <span className="inline-flex items-center gap-1 text-slate-500">
                            <Phone className="w-3 h-3 text-slate-400" />
                            {formatPhone(r.phone)}
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusPill status={r.status} />
                    </td>
                    <td className="px-4 py-3">
                      <HealthCell
                        score={r.health_score || 0}
                        breakdown={r.health_breakdown}
                      />
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums font-semibold text-slate-900">
                      {r.workflow_count}
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-[12px]">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3 text-slate-400" />
                        {_ago(r.last_activity_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600 text-[12px]">
                      <span className="inline-flex items-center gap-1">
                        <CalendarDays className="w-3 h-3 text-slate-400" />
                        {_fmtDate(r.joined_at)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Link
                        to="/admin/clinics"
                        data-testid={`clinic-roster-branding-${r.id}`}
                        className="inline-flex items-center gap-1 bg-white border border-slate-200 text-slate-700 rounded-full px-2.5 py-1 text-[11px] font-semibold hover:bg-slate-50"
                        title="Edit branding for this clinic"
                      >
                        <Palette className="w-3 h-3" /> Branding
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </ToolPageShell>
  );
}

function KpiCard({ icon: Icon, label, value, tone = "slate", testid }) {
  const TONE = {
    slate: "bg-slate-100 text-slate-700",
    emerald: "bg-emerald-100 text-emerald-700",
    amber: "bg-amber-100 text-amber-700",
    blue: "bg-blue-100 text-blue-700",
    rose: "bg-rose-100 text-rose-700",
  };
  return (
    <div
      className="rounded-2xl border border-slate-200 bg-white p-4 flex items-center gap-3"
      data-testid={testid}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 ${TONE[tone]}`}>
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">
          {label}
        </div>
        <div className="text-xl font-bold text-slate-900 tabular-nums leading-tight">{value}</div>
      </div>
    </div>
  );
}
